import { styled } from '@mui/material/styles'
import LinearProgress, {
  linearProgressClasses,
} from '@mui/material/LinearProgress'
import { Box, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'

const BorderLinearProgressComponent = styled(LinearProgress)(({ theme }) => ({
  borderRadius: 4,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    background: '#E4E4E4',
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    background: 'linear-gradient(to right, #000000, #1705E5)',
  },
}))

const RectanglesOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  height: 'calc(100% - 6px)',
  display: 'flex',
  gap: '3px',
  padding: '3px',
  pointerEvents: 'none', // Prevent the overlay from interfering with progress bar functionality
}))

const Rectangle = styled(Box)(({ theme }) => ({
  width: '1rem',
  background: '#1705E5',
  borderRadius: '2px',
}))

interface ProgressBarProps {
  completed: number
  total: number
  height: string
  title?: string
  showPercentage?: boolean
  hideOverlay?: boolean
  hideHeader?: boolean
  hideGradient?: boolean
}

const ProgressBarComponent: React.FC<ProgressBarProps> = ({
  completed,
  total,
  height,
  title,
  showPercentage,
  hideOverlay,
  hideHeader,
  hideGradient,
}) => {
  const progressBarRef = useRef<HTMLDivElement>(null)
  const [progressBarWidth, setProgressBarWidth] = useState(0)

  useEffect(() => {
    if (progressBarRef.current) {
      setProgressBarWidth(progressBarRef.current.offsetWidth)
    }
  }, [])

  const percentage = Math.round((completed / total) * 100)
  const filledWidth = (percentage / 100) * progressBarWidth
  const numberOfRectangles = Math.floor(filledWidth / 19) // 15px width + 2px gap

  return (
    <Box
      display='flex'
      flexDirection='column'
      width='100%'
      gap='0.5rem'
      position='relative'
    >
      {!hideHeader && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Typography
            sx={{
              color: '#797979',
              fontSize: '0.7rem',
              lineHeight: 'normal',
              fontWeight: 500,
            }}
          >
            {title ? title : 'Project Progress'}
          </Typography>
          <Typography
            sx={{
              color: '#04002E',
              fontSize: '1rem',
              lineHeight: 'normal',
              fontWeight: 500,
            }}
          >
            {showPercentage ? `${percentage}%` : `${completed} of ${total}`}
          </Typography>
        </Box>
      )}
      <Box position='relative' width='100%' ref={progressBarRef}>
        <BorderLinearProgressComponent
          variant='determinate'
          value={percentage}
          sx={{ height: height, width: '100%' }}
        />
        {!hideOverlay && (
          <RectanglesOverlay>
            {Array.from({ length: numberOfRectangles }).map((_, index) => (
              <Rectangle key={index} />
            ))}
          </RectanglesOverlay>
        )}
      </Box>
    </Box>
  )
}

export default ProgressBarComponent
